import React, { useState } from 'react';
import { getReport } from '../../services/cardAnalysisService';
import CardUpload from '../../components/cardAnalysis/CardUpload';
import BatchCardUpload from '../../components/cardAnalysis/BatchCardUpload';
import AnalysisProgress from '../../components/cardAnalysis/AnalysisProgress';
import BatchAnalysisProgress from '../../components/cardAnalysis/BatchAnalysisProgress';
import ReportDisplay from '../../components/cardAnalysis/ReportDisplay';
import BatchReportDisplay from '../../components/cardAnalysis/BatchReportDisplay';
import { BusinessCard, CardAnalysisReport, CardAnalysisReportWithData } from '../../types';

enum AnalysisStage {
  UPLOAD = 'upload',
  ANALYZING = 'analyzing',
  REPORT = 'report',
}

enum AnalysisMode {
  SINGLE = 'single',
  BATCH = 'batch',
}

const CardAnalysisPage: React.FC = () => {
  const [mode, setMode] = useState<AnalysisMode>(AnalysisMode.SINGLE);
  const [stage, setStage] = useState<AnalysisStage>(AnalysisStage.UPLOAD);
  const [cardId, setCardId] = useState<string | null>(null);
  const [cardIds, setCardIds] = useState<string[]>([]);
  const [report, setReport] = useState<CardAnalysisReportWithData | null>(null);
  const [reportIds, setReportIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleModeChange = (newMode: AnalysisMode) => {
    if (stage === AnalysisStage.UPLOAD) {
      setMode(newMode);
      setCardId(null);
      setCardIds([]);
      setReport(null);
      setReportIds([]);
      setError(null);
    }
  };

  const handleSingleUploadSuccess = (uploadedCardId: string) => {
    setCardId(uploadedCardId);
    setStage(AnalysisStage.ANALYZING);
  };

  const handleBatchUploadSuccess = (uploadedCards: BusinessCard[]) => {
    const ids = uploadedCards.map(card => card.id);
    setCardIds(ids);
    setStage(AnalysisStage.ANALYZING);
  };

  const handleSingleAnalysisComplete = async (reportId: string) => {
    try {
      const reportData = await getReport(reportId);
      setReport(reportData);
      setStage(AnalysisStage.REPORT);
    } catch (err) {
      setError(`Failed to load report: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleBatchAnalysisComplete = (completedReportIds: string[]) => {
    setReportIds(completedReportIds);
    setStage(AnalysisStage.REPORT);
  };

  const handleStartOver = () => {
    setCardId(null);
    setCardIds([]);
    setReport(null);
    setReportIds([]);
    setError(null);
    setStage(AnalysisStage.UPLOAD);
  };

  const handleError = (err: Error) => {
    setError(`Error: ${err.message}`);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Business Card Analysis</h1>
        <p className="text-gray-600 mt-2">
          Upload business cards to extract information and generate comprehensive background reports.
        </p>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      <div className="mb-6">
        <div className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
            stage === AnalysisStage.UPLOAD ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
          }`}>
            1
          </div>
          <div className={`flex-1 h-1 mx-2 ${
            stage === AnalysisStage.UPLOAD ? 'bg-gray-200' : 'bg-blue-500'
          }`}></div>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
            stage === AnalysisStage.ANALYZING ? 'bg-blue-500 text-white' : 
            stage === AnalysisStage.REPORT ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
          }`}>
            2
          </div>
          <div className={`flex-1 h-1 mx-2 ${
            stage === AnalysisStage.REPORT ? 'bg-blue-500' : 'bg-gray-200'
          }`}></div>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
            stage === AnalysisStage.REPORT ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
          }`}>
            3
          </div>
        </div>
        <div className="flex justify-between mt-2">
          <div className="text-center">
            <span className="text-sm font-medium">Upload {mode === AnalysisMode.BATCH ? 'Cards' : 'Card'}</span>
          </div>
          <div className="text-center">
            <span className="text-sm font-medium">Analyze</span>
          </div>
          <div className="text-center">
            <span className="text-sm font-medium">View {mode === AnalysisMode.BATCH ? 'Reports' : 'Report'}</span>
          </div>
        </div>
      </div>

      {stage === AnalysisStage.UPLOAD && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6">
            <div className="flex border-b border-gray-200">
              <button
                className={`py-2 px-4 font-medium ${
                  mode === AnalysisMode.SINGLE
                    ? 'text-blue-500 border-b-2 border-blue-500'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => handleModeChange(AnalysisMode.SINGLE)}
              >
                Single Card
              </button>
              <button
                className={`py-2 px-4 font-medium ${
                  mode === AnalysisMode.BATCH
                    ? 'text-blue-500 border-b-2 border-blue-500'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => handleModeChange(AnalysisMode.BATCH)}
              >
                Batch Upload
              </button>
            </div>
          </div>

          {mode === AnalysisMode.SINGLE ? (
            <CardUpload onUploadSuccess={handleSingleUploadSuccess} />
          ) : (
            <BatchCardUpload onUploadComplete={handleBatchUploadSuccess} onError={handleError} />
          )}
        </div>
      )}

      {stage === AnalysisStage.ANALYZING && (
        <div className="bg-white rounded-lg shadow-md p-6">
          {mode === AnalysisMode.SINGLE && cardId ? (
            <AnalysisProgress 
              cardId={cardId} 
              onAnalysisComplete={handleSingleAnalysisComplete} 
              onError={handleError}
            />
          ) : mode === AnalysisMode.BATCH && cardIds.length > 0 ? (
            <BatchAnalysisProgress 
              cardIds={cardIds} 
              onAnalysisComplete={handleBatchAnalysisComplete} 
              onError={handleError}
            />
          ) : (
            <div className="text-center py-8">
              <p className="text-red-500">No cards selected for analysis.</p>
              <button
                onClick={handleStartOver}
                className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                Start Over
              </button>
            </div>
          )}
        </div>
      )}

      {stage === AnalysisStage.REPORT && (
        <div className="bg-white rounded-lg shadow-md p-6">
          {mode === AnalysisMode.SINGLE && report ? (
            <>
              <ReportDisplay report={report} />
              <div className="mt-6 text-center">
                <button
                  onClick={handleStartOver}
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Analyze Another Card
                </button>
              </div>
            </>
          ) : mode === AnalysisMode.BATCH && reportIds.length > 0 ? (
            <BatchReportDisplay reportIds={reportIds} onStartOver={handleStartOver} />
          ) : (
            <div className="text-center py-8">
              <p className="text-red-500">No reports available.</p>
              <button
                onClick={handleStartOver}
                className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                Start Over
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CardAnalysisPage;
